import { Label } from '../ui/label.tsx';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group.tsx';
import { CustomerPicker } from '../CustomerPicker.tsx';
import type { AssignedProperty, Customer, CustomerRbacRole } from '@metron/shared';
import { CUSTOMER_RBAC_ROLE_LABELS, isPropertyManagerRole, rolesForCustomer } from '@metron/shared';
import { PropertyMultiSelect } from './PropertyMultiSelect.tsx';

export interface CustomerRoleStepProps {
  customers: Customer[];
  selectedCustomerId: number | null;
  onCustomerChange: (customerId: number) => void;
  /** Clears the current customer selection (and any dependent role state). */
  onCustomerClear?: () => void;
  /**
   * When true the customer is fixed by the caller: the picker still renders and
   * still shows the selected customer, but it cannot be searched or cleared.
   * "Shown but disabled" — MET-3237 rows 8 and 13.
   */
  customerLocked?: boolean;
  selectedRole: string | null;
  onRoleChange: (role: string) => void;
  /** Roles the signed-in user may grant for the selected customer (already filtered). */
  grantable: CustomerRbacRole[];
  disabled?: boolean;
  /** All properties for the selected customer; only shown for a property-manager role. */
  availableProperties?: AssignedProperty[];
  loadingProperties?: boolean;
  selectedPropertyIds?: number[];
  onPropertyToggle?: (propertyId: number, checked: boolean) => void;
}

export function CustomerRoleStep(props: CustomerRoleStepProps) {
  const {
    customers,
    selectedCustomerId,
    onCustomerChange,
    onCustomerClear,
    customerLocked = false,
    selectedRole,
    onRoleChange,
    grantable,
    disabled,
    availableProperties = [],
    loadingProperties = false,
    selectedPropertyIds = [],
    onPropertyToggle,
  } = props;
  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId) ?? null;
  const isPropertyMgr = isPropertyManagerRole(selectedRole);
  // Roles the account type defines at all, before permission filtering. Lets us
  // tell "no roles exist for this account type" apart from "you can't grant them".
  const candidateRoles = selectedCustomer
    ? rolesForCustomer(selectedCustomer.accountType, selectedCustomer.realEstateAccountType)
    : [];

  return (
    <div className='space-y-4'>
      <div>
        <Label className='text-sm font-medium mb-2 block'>Customer</Label>
        <CustomerPicker
          customers={customers}
          selectedCustomerId={selectedCustomerId}
          onSelect={(id) => onCustomerChange(id)}
          onClear={customerLocked ? undefined : onCustomerClear}
          isLoading={disabled}
          disabled={customerLocked}
        />
      </div>

      {!selectedCustomer && (
        <p className='text-sm text-muted-foreground'>Select a customer for this user.</p>
      )}

      {selectedCustomer && candidateRoles.length === 0 && (
        <p className='text-sm text-destructive'>
          No roles are available for this account type.
        </p>
      )}

      {selectedCustomer && candidateRoles.length > 0 && grantable.length === 0 && (
        <p className='text-sm text-destructive'>
          You don't have permission to assign a role for this account type.
        </p>
      )}

      {selectedCustomer && grantable.length === 1 && (
        <div>
          <Label className='text-sm font-medium mb-2 block'>Role</Label>
          <p className='text-sm bg-muted rounded-md p-2'>
            {CUSTOMER_RBAC_ROLE_LABELS[grantable[0]]} — set automatically for{' '}
            {selectedCustomer.accountType} accounts
          </p>
        </div>
      )}

      {selectedCustomer && grantable.length > 1 && (
        <div>
          <Label className='text-sm font-medium mb-2 block'>Role</Label>
          <RadioGroup
            value={selectedRole ?? ''}
            onValueChange={onRoleChange}
            disabled={disabled}
            data-testid='role-picker'
          >
            <div className='flex flex-col gap-2'>
              {grantable.map((role) => (
                <div key={role} className='flex items-center gap-2'>
                  <RadioGroupItem value={role} id={`role-${role}`} />
                  <Label htmlFor={`role-${role}`} className='cursor-pointer'>
                    {CUSTOMER_RBAC_ROLE_LABELS[role]}
                  </Label>
                </div>
              ))}
            </div>
          </RadioGroup>
        </div>
      )}

      {selectedCustomer && isPropertyMgr && onPropertyToggle && (
        <PropertyMultiSelect
          availableProperties={availableProperties}
          loadingProperties={loadingProperties}
          selectedPropertyIds={selectedPropertyIds}
          onPropertyToggle={onPropertyToggle}
          disabled={disabled}
        />
      )}
    </div>
  );
}
